import React from 'react';
import ReactDOM from 'react-dom';

// Styles & Assets
import styles from './PatientPharmacyPage.module.css';

// Services
import { createAdministrationsList, getOrderedDatesFromAdministrationList } from '../../services/Pharmacy';
import { getEnvironmentApiUrl } from '../../services/General';
import { sortArray } from '../../services/sort';

// Hooks
import { useState, useEffect, useCallback } from 'react';
import useHttp from '../../hooks/useHttp';

// Components
import LegendPharmacy from '../../components/PatientPharmacyPage/LegendPharmacy/LegendPharmacy';
import TablePharmacy from '../../components/PatientPharmacyPage/TablePharmacy/TablePharmacy';
import Searchbar from '../../components/UI/Searchbar/Searchbar';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';

// Interfaces
import { DrugAdministration, DrugAdministrationList } from '../../interfaces/Drug';
import { FilterGeneric } from '../../interfaces/Filters';
import { PatientOutletContext } from '../../interfaces/Patient';

// Other
import { useOutletContext } from 'react-router-dom';
import { canEditCaseView } from '../../services/user';

const PatientPharmacyPage: React.FC = () => {
    const { caso }: PatientOutletContext = useOutletContext();

    const [filterDrugs, setFilterDrugs] = useState<FilterGeneric<DrugAdministrationList>>(() => () => () => true);

    const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        const hasCoincidence = (element: string) =>
            element.toLocaleLowerCase().includes(evt.target.value.toLocaleLowerCase());
        setFilterDrugs(() => () => (drug: DrugAdministrationList) =>
            hasCoincidence(drug.drugName));
    };

    const { error, sendRequest } = useHttp<DrugAdministration[]>();

    const [optionalDrugs, setOptionalDrugs] = useState<DrugAdministrationList[]>([]);
    const [mandatoryDrugs, setMandatoryDrugs] = useState<DrugAdministrationList[]>([]);
    const [dates, setDates] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    const getTodayDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const parseDrugAdministration = useCallback((drugs: DrugAdministration[]) => {
    const drugsMandatory = createAdministrationsList(drugs.filter(result => !result.optional));
    const drugsOptional = createAdministrationsList(drugs.filter(result => result.optional));
    const orderedDates = getOrderedDatesFromAdministrationList([...drugsMandatory, ...drugsOptional]);
    const today = getTodayDate();
    const nextDates = orderedDates.includes(today) ? orderedDates : sortArray([...orderedDates, today], false);

    setMandatoryDrugs(drugsMandatory);
    setOptionalDrugs(drugsOptional);
    setDates(nextDates);
    setLoading(false);
}, []);

    useEffect(() => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'drug_administration/all',
            method: 'POST',
            body: {
                params: {},
                filter: { patient_id: caso.patient_id },
            }
        }, (data) => parseDrugAdministration(data), () => setLoading(false));
    }, [caso.patient_id, sendRequest, parseDrugAdministration]);

    const handleSetDrugs = (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => {
        setMandatoryDrugs(updateDrugs);
    };

    const handleSetOptionalDrugs = (updateDrugs: (oldDrugs: DrugAdministrationList[]) => DrugAdministrationList[]) => {
        setOptionalDrugs(updateDrugs);
    };

    const filteredMandatoryDrugs = mandatoryDrugs
        .filter(drug => filterDrugs()(drug));
    const filteredOptionalDrugs = optionalDrugs
        .filter(drug => filterDrugs()(drug));

    const searchbarElement = document.getElementById('patient-buttons-right') as HTMLElement;

    const readOnly = !canEditCaseView(caso);

    return (
        <div className={styles.container}>
            {searchbarElement && ReactDOM.createPortal(
                <div>
                    <Searchbar handleChange={handleChange} />
                </div>,
                searchbarElement,
            )}
            <ErrorSpinnerWrapper
                loading={loading}
                error={error}
            >
                <TablePharmacy
                    mandatoryDrugs={filteredMandatoryDrugs}
                    optionalDrugs={filteredOptionalDrugs}
                    dates={dates}
                    handleSetDrugs={handleSetDrugs}
                    handleSetOptionalDrugs={handleSetOptionalDrugs}
                    readOnly={readOnly}
                />
            </ErrorSpinnerWrapper>
            <div className={styles.bottomRow}>
                <LegendPharmacy />
                {/* <button disabled onClick={() => setShowPrescribeDialog(true)} className={`button ${styles.prescribeButton}`}>
                    {t('prescribe')}
                </button> */}
            </div>
        </div>
    );
};

export default PatientPharmacyPage;